import React, { useEffect, useState } from "react";
import axios from "../utils/axiosInstance";
import { useNavigate } from "react-router-dom";

const emptyAddress = {
    label: "",
    fullName: "",
    phone: "",
    country: "",
    city: "",
    addressLine: "",
    postalCode: "",
    isDefault: false,
};

const isValidIraqiPhone = (number) => /^7[4-9][0-9]{8}$/.test(number);

const MyAddressesPage = () => {
    const navigate = useNavigate();
    const [addresses, setAddresses] = useState([]);
    const [form, setForm] = useState(emptyAddress);
    const [editingIdx, setEditingIdx] = useState(null);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        axios.get("/users/addresses").then((res) => setAddresses(res.data));
    }, []);

    const handleFormChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleAdd = () => {
        setEditingIdx(null);
        setForm(emptyAddress);
        setShowForm(true);
    };

    const handleEdit = (idx) => {
        setEditingIdx(idx);
        setForm(addresses[idx]);
        setShowForm(true);
    };

    const handleDelete = async (idx) => {
        if (!window.confirm("Delete this address?")) return;
        await axios.delete(`/users/addresses/${idx}`);
        setAddresses((prev) => prev.filter((_, i) => i !== idx));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const phoneToSave = "+964" + form.phone;
        const formToSend = { ...form, phone: phoneToSave };

        if (editingIdx !== null) {
            // Edit
            const res = await axios.put(`/users/addresses/${editingIdx}`, formToSend);
            setAddresses(res.data);
        } else {
            // Add
            const res = await axios.post("/users/addresses", formToSend);
            setAddresses(res.data);
        }
        setShowForm(false);
        setForm(emptyAddress);
    };


    return (
        <div className="bg-gray-50 min-h-screen py-10 flex flex-col items-center">
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl p-6">
                <h2 className="text-xl font-semibold mb-4">My Addresses</h2>
                <button
                    className="mb-4 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800"
                    onClick={handleAdd}
                >
                    + Add Address
                </button>
                <div className="space-y-4">
                    {addresses.map((addr, idx) => (
                        <div
                            key={idx}
                            className={`p-4 border rounded-xl ${addr.isDefault ? "border-black" : "border-gray-200"} flex flex-col md:flex-row justify-between items-start md:items-center`}
                        >
                            <div>
                                <div className="font-bold">{addr.label || "Address"}</div>
                                <div>{addr.fullName} • {addr.phone}</div>
                                <div>{addr.addressLine}</div>
                                <div>
                                    {addr.city}, {addr.country} {addr.postalCode}
                                </div>
                                {addr.isDefault && (
                                    <span className="inline-block px-2 py-1 mt-1 text-xs bg-black text-white rounded">Default</span>
                                )}
                            </div>
                            <div className="flex gap-2 mt-2 md:mt-0">
                                <button
                                    className="text-blue-600 hover:underline"
                                    onClick={() => handleEdit(idx)}
                                >
                                    Edit
                                </button>
                                <button
                                    className="text-rose-600 hover:underline"
                                    onClick={() => handleDelete(idx)}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {showForm && (
                    <form className="mt-6 space-y-3" onSubmit={handleSave}>
                        <div className="flex gap-3">
                            <input
                                className="w-1/3 px-3 py-2 border rounded"
                                name="label"
                                placeholder="Label (Home/Work)"
                                value={form.label}
                                onChange={handleFormChange}
                            />
                            <input
                                className="w-2/3 px-3 py-2 border rounded"
                                name="fullName"
                                placeholder="Full Name"
                                value={form.fullName}
                                onChange={handleFormChange}
                                required
                            />
                        </div>
                        <div className="w-1/2 flex gap-1">
                            <span className="px-2 py-2 border rounded bg-gray-100 self-center">+964</span>
                            <input
                                className="w-full px-3 py-2 border rounded"
                                name="phone"
                                placeholder="7xxxxxxxxx"
                                value={form.phone}
                                onChange={e => {
                                    // Accept only digits and remove leading zero
                                    let val = e.target.value.replace(/\D/g, "");
                                    if (val.startsWith("0")) val = val.slice(1);
                                    setForm({ ...form, phone: val });
                                }}
                                maxLength={10}
                                required
                            />
                        </div>
                        {form.phone && !isValidIraqiPhone(form.phone) && (
                            <div className="text-rose-600 text-xs mt-1">
                                Please enter a valid Iraqi mobile number.
                            </div>
                        )}

                        <div className="flex gap-3">
                            <input
                                className="w-1/2 px-3 py-2 border rounded"
                                name="city"
                                placeholder="City"
                                value={form.city}
                                onChange={handleFormChange}
                                required
                            />
                            <input
                                className="w-1/2 px-3 py-2 border rounded"
                                name="postalCode"
                                placeholder="Postal Code"
                                value={form.postalCode}
                                onChange={handleFormChange}
                            />
                        </div>
                        <input
                            className="w-full px-3 py-2 border rounded"
                            name="addressLine"
                            placeholder="Address"
                            value={form.addressLine}
                            onChange={handleFormChange}
                            required
                        />
                        <div>
                            <label className="inline-flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    name="isDefault"
                                    checked={form.isDefault}
                                    onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))}
                                />
                                Set as default
                            </label>
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                className="flex-1 bg-black text-white py-2 rounded hover:bg-gray-800"
                            >
                                Save
                            </button>
                            <button
                                type="button"
                                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded"
                                onClick={() => setShowForm(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default MyAddressesPage;
